import { useLife } from '@/state/life';
import { useMarkets } from '@/state/markets';
import { usePower } from '@/state/power';
import { useXClone } from '@/state/xclone';
import { usePhone } from '@/state/phone';
import { useNotifications } from '@/state/notifications';
import { useCinematic } from '@/components/CinematicTakeover';
import { useMeta } from '@/state/meta';
import { sounds } from '@/audio/synth';
import { PHONE_ACTION_HANDLERS } from './phoneActions';
import { addImpulse } from './prices';

/**
 * Hostile Takeover mini-arc.
 *
 * Late-game arc when cash >= $5M. You target a competitor (RUGS).
 * 4 board-room decisions decide whether you walk away with the company
 * (huge influence + tribal rep) or get crushed under a $1M failed bid.
 *
 * High stakes. Real impulses fire on the target ticker mid-arc.
 */

interface Beat {
  text: string;
  options: { label: string; effect: () => number }[]; // returns "deal score" delta
}

const BEATS: Beat[] = [
  {
    text: 'YOUR CFO: "first move on $RUGS — what\'s the opening bid?"',
    options: [
      { label: 'tender offer at 30% premium', effect: () => {
        useMarkets.getState().addCash(-800_000);
        addImpulse({ ticker: 'RUGS', startMs: Date.now(), durationMs: 8 * 60_000, logDelta: 0.18 });
        return 2;
      } },
      { label: 'quietly accumulate via dark pool', effect: () => {
        useMarkets.getState().addCash(-300_000);
        addImpulse({ ticker: 'RUGS', startMs: Date.now(), durationMs: 6 * 60_000, logDelta: 0.06 });
        useLife.getState().bumpSecHeat(8);
        return 1;
      } },
      { label: 'leak to the press first to soften the board', effect: () => {
        usePower.getState().bumpRep('press', 6);
        addImpulse({ ticker: 'RUGS', startMs: Date.now(), durationMs: 4 * 60_000, logDelta: 0.10 });
        return 1;
      } },
    ],
  },
  {
    text: '$RUGS BOARD adopts a "poison pill" defense. CFO: "they\'re trying to dilute you out. counter?"',
    options: [
      { label: 'sue them. allege breach of fiduciary duty.', effect: () => {
        useMarkets.getState().addCash(-200_000);
        usePower.getState().bumpRep('press', 4);
        return 2;
      } },
      { label: 'go directly to shareholders with a tender offer at 50% premium', effect: () => {
        useMarkets.getState().addCash(-1_500_000);
        usePower.getState().bumpInfluence(8);
        return 3;
      } },
      { label: 'walk away. concede.', effect: () => {
        useLife.getState().bumpSanity(2);
        return -2;
      } },
    ],
  },
  {
    text: 'YOUR LAWYER: "their CEO wants a private meeting. probably wants a golden parachute. what do you offer?"',
    options: [
      { label: '$15M severance + a board seat in your foundation', effect: () => {
        useMarkets.getState().addCash(-15_000_000);
        usePower.getState().bumpInfluence(12);
        return 3;
      } },
      { label: '$2M severance and a non-disclosure that\'s 47 pages long', effect: () => {
        useMarkets.getState().addCash(-2_000_000);
        usePower.getState().bumpRep('press', -5);
        return 1;
      } },
      { label: '$0 and threaten to publicize his crypto wallet', effect: () => {
        usePower.getState().bumpNotoriety(15);
        useLife.getState().bumpFbiHeat(5);
        return 2;
      } },
    ],
  },
  {
    text: 'FINAL VOTE — your CFO is on the phone with the proxy advisors. last move?',
    options: [
      { label: 'fly the swing-vote shareholder to your yacht. now.', effect: () => {
        useMarkets.getState().addCash(-50_000);
        usePower.getState().bumpInfluence(6);
        return 2;
      } },
      { label: 'commit publicly to "no layoffs" — get the union\'s endorsement', effect: () => {
        usePower.getState().bumpRep('left', 8);
        usePower.getState().bumpRep('press', -6);
        return 2;
      } },
      { label: 'do nothing. you\'ve done enough.', effect: () => { return 0; } },
    ],
  },
];

let dealScore = 0;

export function startHostileTakeoverArc(): void {
  dealScore = 0;
  useMeta.getState().recordLore('arc-hostile-takeover', 'Hostile Takeover');
  useCinematic.getState().fire({
    id: `takeover-${Date.now()}`,
    type: 'breaking',
    headline: 'HOSTILE TAKEOVER LAUNCHED.',
    subhead: 'target: $RUGS. 4 board moves. you either own them by Friday or you\'re a meme by Saturday.',
    durationMs: 5500,
  });
  sounds.notifyAlert();

  for (let i = 0; i < BEATS.length; i++) {
    const b = BEATS[i]!;
    setTimeout(() => {
      const ids = ['a', 'b', 'c'];
      const actions = b.options.map((opt, idx) => ({
        id: ids[idx]!,
        label: opt.label,
        effectId: `takeover-q${i}-${ids[idx]}`,
      }));
      for (let j = 0; j < b.options.length; j++) {
        const id = `takeover-q${i}-${ids[j]}`;
        const opt = b.options[j]!;
        PHONE_ACTION_HANDLERS[id] = () => {
          dealScore += opt.effect();
          sounds.fill();
        };
      }
      usePhone.getState().receiveMessage({
        threadId: 'broker',
        fromNpc: 'broker',
        text: b.text,
        actions,
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: `takeover: move ${i + 1}/4` });
      sounds.notifyAlert();
    }, 6_000 + i * 35_000);
  }

  setTimeout(() => {
    if (dealScore >= 7) {
      useCinematic.getState().fire({
        id: `takeover-win-${Date.now()}`,
        type: 'ascension',
        headline: '$RUGS IS YOURS.',
        subhead: 'you now control all 14 operating subsidiaries. influence +50. you also inherited a class-action lawsuit and 47 angry employees.',
        durationMs: 7000,
      });
      usePower.getState().bumpInfluence(50);
      usePower.getState().bumpNotoriety(20);
      addImpulse({ ticker: 'RUGS', startMs: Date.now(), durationMs: 30 * 60_000, logDelta: 0.45 });
      useXClone.getState().receivePost({
        authorHandle: 'WSJ_RealTimeBait', authorTribe: 'press',
        text: 'BREAKING: a "well-known retail trader" has successfully completed the most chaotic hostile takeover in modern memory. analysts call it "concerning but inevitable." stock up 60%.',
        likes: 41000, reposts: 12500,
      });
    } else {
      useCinematic.getState().fire({
        id: `takeover-loss-${Date.now()}`,
        type: 'breaking',
        headline: 'TAKEOVER FAILED.',
        subhead: 'their board outmaneuvered you. estimated cost of failed bid: \$1M+. opposition CEO is now doing a victory lap on CNBC.',
        durationMs: 6500,
      });
      useMarkets.getState().addCash(-1_000_000);
      useLife.getState().bumpSanity(-15);
      usePower.getState().bumpRep('press', -10);
      useXClone.getState().receivePost({
        authorHandle: 'CNBCSquawk', authorTribe: 'cnbc',
        text: 'breaking: amateur hour. retail trader\'s hostile bid for $RUGS collapses spectacularly. analysts: "we\'re not even sure what just happened."',
        likes: 8400, reposts: 2100,
      });
    }
  }, 6_000 + BEATS.length * 35_000 + 5_000);
}
